import { useCallback, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Icon from "@/v2-ui/icon"
import Button from "@/v2-backoffice-client/components/button"
import UserEntraButton, {
  getUserEntraUrl,
} from "@/v2-backoffice-client/user/UserEntraButton"
import { useUserRecreateIdentityMutation } from "@/v2-backoffice-client/user/recreateIdentity/user.recreateIdentity.mutation"
import UserRecreateIdentityForm, {
  type UserRecreateIdentityFormValues,
} from "@/v2-backoffice-client/user/recreateIdentity/UserRecreateIdentityForm"
import type { UserRecreateIdentityMutation } from "@/v2-backoffice-client/user/recreateIdentity/__types__/user.recreateIdentity.mutation"

type RecreateResult = UserRecreateIdentityMutation["recreateUserIdentity"]

export type UserRecreateIdentityDialogProps = DialogProps & {
  userId: number
  defaultEmail?: string
}

function UserRecreateIdentityDialog(props: UserRecreateIdentityDialogProps) {
  const { userId, defaultEmail, open, onOpenChange, ...dialogProps } = props
  const [isFormValid, setIsFormValid] = useState(false)
  const [result, setResult] = useState<RecreateResult | null>(null)
  // When the target email is already owned by another LOCAL Azure sign-in,
  // recreate returns its id for confirmation; we stash it + the submitted
  // values so the operator can adopt it (re-link the DB row onto it + reset
  // its password). A non-local holder (e.g. a federated guest) is left alone
  // server-side, so it never reaches this confirmation.
  const [conflict, setConflict] = useState<{
    existingAzureId: string
    values: UserRecreateIdentityFormValues
  } | null>(null)

  const [recreateIdentity, { loading, error, reset }] =
    useUserRecreateIdentityMutation()

  const handleValidityChange = useCallback((isValid: boolean) => {
    setIsFormValid(isValid)
  }, [])

  function handleOpenChange(next: boolean) {
    if (!next) {
      setResult(null)
      setConflict(null)
      setIsFormValid(false)
      reset()
    }
    onOpenChange?.(next)
  }

  async function submitRecreate(
    values: UserRecreateIdentityFormValues,
    confirmedAzureId?: string,
  ) {
    const res = await recreateIdentity({
      variables: {
        input: {
          Id: userId,
          Email: values.email,
          Password: values.password || undefined,
          SendAccountEmail: values.sendAccountEmail || undefined,
          ConfirmedAzureId: confirmedAzureId,
        },
      },
    })
    const recreated = res.data?.recreateUserIdentity
    if (!recreated) return
    if (recreated.User) {
      setResult(recreated)
      setConflict(null)
      return
    }
    if (recreated.ExistingAzureId) {
      setConflict({
        existingAzureId: recreated.ExistingAzureId,
        values,
      })
    }
  }

  const newUser = result?.User
  const showForm = !newUser && !conflict

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        {showForm && (
          <>
            <Dialog.Header>
              <Dialog.Title>Recreate login</Dialog.Title>
              <Dialog.Description>
                Provisions a brand-new Azure email login for this user and
                re-links their account to it. Use this when the login email
                can&apos;t be changed in place. The old Azure identity is
                deleted and any active sessions are revoked — the user must sign
                in again with the new email and temporary password.
              </Dialog.Description>
            </Dialog.Header>

            <ErrorMessage className="my-4 text-center" error={error} />

            <UserRecreateIdentityForm
              defaultEmail={defaultEmail}
              onValidityChange={handleValidityChange}
              onSubmit={async (values, e) => {
                e.preventDefault()
                await submitRecreate(values)
              }}
            />

            <Dialog.Footer>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleOpenChange(false)
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                isLoading={loading}
                disabled={!isFormValid}
                form="recreate-identity-form"
              >
                Recreate Login
              </Button>
            </Dialog.Footer>
          </>
        )}

        {conflict && (
          <>
            <Dialog.Header>
              <Dialog.Title>Email already has a login</Dialog.Title>
              <Dialog.Description>
                {conflict.values.email} already has a local Azure login. Instead
                of creating a new one, this user&apos;s account will be
                re-linked onto it (its password reset to the temporary one
                above) and their old Azure identity deleted. Active sessions are
                revoked.
              </Dialog.Description>
            </Dialog.Header>

            <ErrorMessage className="my-4 text-center" error={error} />

            <div className="py-4 space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Azure ID:</span>
                <code className="text-xs bg-muted px-2 py-0.5 rounded">
                  {conflict.existingAzureId}
                </code>
              </div>
              <a
                href={getUserEntraUrl(conflict.existingAzureId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-primary hover:underline"
              >
                <Icon src="entra" size={14} />
                View in Entra
              </a>
            </div>

            <Dialog.Footer>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setConflict(null)
                  reset()
                }}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="destructive"
                isLoading={loading}
                onClick={async () => {
                  await submitRecreate(
                    conflict.values,
                    conflict.existingAzureId,
                  )
                }}
              >
                Adopt this identity
              </Button>
            </Dialog.Footer>
          </>
        )}

        {newUser && (
          <>
            <Dialog.Header>
              <Dialog.Title>Login recreated</Dialog.Title>
            </Dialog.Header>

            <div className="py-4 space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">
                    {newUser.FirstName} {newUser.LastName}
                  </span>
                  <UserEntraButton azureId={newUser.AzureID} />
                </div>
                <div className="text-muted-foreground">{newUser.Email}</div>
                <div className="text-xs text-muted-foreground">
                  ID: {newUser.Id} · Azure ID: {newUser.AzureID}
                </div>
              </div>

              {result?.GeneratedPassword && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Temporary password</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-sm bg-muted px-3 py-2 rounded font-mono select-all">
                      {result.GeneratedPassword}
                    </code>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconSrc="copy"
                      onClick={() => {
                        navigator.clipboard.writeText(
                          result.GeneratedPassword ?? "",
                        )
                      }}
                    >
                      Copy
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The user signs in with the new email and is prompted to
                    change this password on first sign-in.
                  </p>
                </div>
              )}
            </div>

            <Dialog.Footer>
              <Button
                type="button"
                onClick={() => {
                  handleOpenChange(false)
                }}
              >
                Done
              </Button>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  )
}

export default UserRecreateIdentityDialog
